(() => {
  const PAGE_LOCK_CLASS = "backlight-bleed-page-active";

  function clampGray(value) {
    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) {
      return 0;
    }

    return Math.min(255, Math.max(0, Math.round(numericValue)));
  }

  function getFullscreenElement() {
    return document.fullscreenElement || document.webkitFullscreenElement || null;
  }

  function isNodeFullscreen(node) {
    const fullscreenElement = getFullscreenElement();

    return Boolean(fullscreenElement && (fullscreenElement === node || node.contains(fullscreenElement)));
  }

  function requestNodeFullscreen(node) {
    if (node.requestFullscreen) {
      return node.requestFullscreen();
    }

    if (node.webkitRequestFullscreen) {
      return node.webkitRequestFullscreen();
    }

    return Promise.resolve();
  }

  function exitBrowserFullscreen() {
    if (document.exitFullscreen) {
      return document.exitFullscreen();
    }

    if (document.webkitExitFullscreen) {
      return document.webkitExitFullscreen();
    }

    return Promise.resolve();
  }

  function lockPage() {
    document.documentElement.classList.add(PAGE_LOCK_CLASS);
    document.body.classList.add(PAGE_LOCK_CLASS);
  }

  function unlockPage() {
    document.documentElement.classList.remove(PAGE_LOCK_CLASS);
    document.body.classList.remove(PAGE_LOCK_CLASS);
  }

  function initBacklightBleedTool(node) {
    if (!node || node.dataset.bleedToolInitialized === "true") {
      return;
    }

    node.dataset.bleedToolInitialized = "true";

    const slider = node.querySelector("[data-bleed-slider]");
    const intro = node.querySelector("[data-bleed-intro]");
    const testUi = node.querySelector("[data-bleed-test-ui]");
    const stage = node.querySelector("[data-bleed-stage]");
    const valueOutput = node.querySelector("[data-bleed-value]");
    const announcer = node.querySelector("[data-bleed-announcer]");
    const fullscreenButton = node.querySelector('[data-bleed-action="fullscreen"]');
    const defaultGray = clampGray(node.dataset.defaultGray);

    if (!slider) {
      return;
    }

    let hasStarted = false;

    function setGray(value, shouldAnnounce = false) {
      const gray = clampGray(value);
      const progress = `${(gray / 255) * 100}%`;

      slider.value = String(gray);
      node.style.setProperty("--bleed-gray-value", String(gray));
      node.style.setProperty("--bleed-progress", progress);

      if (valueOutput) {
        valueOutput.textContent = String(gray);
      }

      if (stage) {
        stage.setAttribute("aria-label", `Backlight bleed test screen at gray level ${gray}`);
      }

      if (shouldAnnounce && announcer) {
        announcer.textContent = `Gray level ${gray} of 255`;
      }
    }

    function syncFullscreenButton() {
      if (!fullscreenButton) {
        return;
      }

      const isActive = isNodeFullscreen(node);
      const defaultLabel = fullscreenButton.dataset.labelDefault || "Fullscreen";
      const activeLabel = fullscreenButton.dataset.labelActive || "Exit Fullscreen";

      fullscreenButton.classList.toggle("is-active", isActive);
      fullscreenButton.setAttribute("aria-pressed", String(isActive));
      fullscreenButton.textContent = isActive ? activeLabel : defaultLabel;
    }

    function syncStage() {
      node.classList.toggle("is-testing", hasStarted);

      if (hasStarted) {
        lockPage();
      } else {
        unlockPage();
      }

      if (intro) {
        intro.setAttribute("aria-hidden", String(hasStarted));
      }

      if (testUi) {
        testUi.setAttribute("aria-hidden", String(!hasStarted));
      }

      syncFullscreenButton();
    }

    function startTest(requestFullscreenNow = false) {
      hasStarted = true;
      setGray(defaultGray, true);
      syncStage();

      if (requestFullscreenNow) {
        Promise.resolve(requestNodeFullscreen(node)).catch(() => {});
      }
    }

    function exitTest() {
      hasStarted = false;
      setGray(defaultGray, true);

      if (isNodeFullscreen(node)) {
        Promise.resolve(exitBrowserFullscreen()).catch(() => {});
      }

      syncStage();
    }

    slider.addEventListener("input", () => {
      setGray(slider.value, true);
    });

    node.addEventListener("click", (event) => {
      const actionTarget = event.target.closest("[data-bleed-action]");

      if (!actionTarget) {
        return;
      }

      event.preventDefault();

      if (actionTarget.dataset.bleedAction === "start") {
        startTest(true);
        return;
      }

      if (actionTarget.dataset.bleedAction === "reset") {
        setGray(defaultGray, true);
        return;
      }

      if (actionTarget.dataset.bleedAction === "fullscreen") {
        if (isNodeFullscreen(node)) {
          Promise.resolve(exitBrowserFullscreen()).catch(() => {});
        } else {
          Promise.resolve(requestNodeFullscreen(node)).catch(() => {});
        }

        return;
      }

      if (actionTarget.dataset.bleedAction === "exit") {
        exitTest();
      }
    });

    const handleFullscreenChange = () => {
      syncFullscreenButton();
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

    window.addEventListener(
      "pagehide",
      () => {
        document.removeEventListener("fullscreenchange", handleFullscreenChange);
        document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
        unlockPage();
      },
      { once: true }
    );

    setGray(defaultGray);
    syncStage();
  }

  function initAll(root = document) {
    root.querySelectorAll("[data-bleed-tool]").forEach(initBacklightBleedTool);
  }

  initAll(document);
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
